// QuickBooks Online UI (task 5.4) — Req 7.5, 7.6, 10.3, NFR6.1.
//
// The Phase 5 backend (tasks 5.1–5.3) exposes:
//   GET  /quickbooks/connect     → { authorizeUrl, environment }  (redirect the
//                                    browser to Intuit's consent screen)
//   GET  /quickbooks/status      → { connected, realmId?, environment?,
//                                    connectedAt?, accessTokenExpiresAt?,
//                                    refreshTokenExpiresAt? }
//   POST /quickbooks/disconnect  → { connected:false }
//   POST /quotes/{id}/push       → the updated quote with synced:true +
//                                    qbEstimateId + syncToken (Estimate)
//   POST /invoices/{id}/push     → the updated invoice with synced:true +
//                                    qbInvoiceId + syncToken, status
//                                    pushed-to-QuickBooks (Invoice)
//   On push failure: 409 { reconnectRequired:true, error } (prompt reconnect) or
//   502 { retryable:true, error, detail } — the LOCAL document is left unchanged.
//
// This module holds everything QuickBooks-facing on the frontend so the pattern is
// identical everywhere:
//   • quickbooksSettingsSection() — the Settings connection panel (Req 10.3).
//   • quickbooksPushCard()        — the confirm-then-push card reused by the quote
//                                    and invoice detail screens (Req 7.5, 7.6).
//   • parseQuickBooksReturn()/…ReturnStatus — handle the /settings?quickbooks=…
//                                    banner the OAuth callback redirects back with.
//
// CONFIRM-THEN-PUSH (NFR6.1): pushing to QuickBooks is never automatic. The card
// shows an explicit two-step confirm — the electrician taps "Push to QuickBooks",
// reads what will happen, then taps "Confirm push". Only then is POST …/push
// called. This mirrors the human-in-the-loop rule the AI drafting screens follow.
//
// SANDBOX vs PRODUCTION (task 5.4): which QuickBooks company Sparky talks to is a
// BACKEND, stage-driven choice — the `app` stage is wired to the QuickBooks
// **sandbox** company and `prod` to the **production** company, via the
// per-stage SSM `environment` parameter the connect/status endpoints read (see
// api/handlers/quickbooks.js and design.md QuickBooks section). The frontend only
// REFLECTS that: GET /quickbooks/status returns `environment`, which the Settings
// panel shows as a Sandbox/Production badge so it's obvious which company a given
// deployment is connected to. There is nothing environment-specific to configure
// in the UI itself.

import { api } from '../api.js';
import {
  h, mount, runAsync, routeToHash,
  card, detailList, muted,
  button, buttonRow, errorSlot, setError, runAction,
  statusBadge, chip, shortDate,
  parseQuickBooksReturn, setQuickBooksReturnStatus, takeQuickBooksReturnStatus,
  pushErrorInfo, environmentLabel,
} from './ui.js';

// The pure helpers live in ui.js (no api/Cognito import, unit-tested there);
// re-exported here so app.js and the detail screens import all things QuickBooks
// from one place.
export {
  parseQuickBooksReturn,
  setQuickBooksReturnStatus,
  takeQuickBooksReturnStatus,
  pushErrorInfo,
  environmentLabel,
};

// --- Settings connection panel (Req 10.3) ----------------------------------

// A small labelled banner (connect success / error) shown once after the OAuth
// redirect returns to Settings.
function returnBanner(status) {
  if (status === 'connected') {
    return h('div', { class: 'qb-banner qb-banner-success', role: 'status' }, [
      'QuickBooks connected.',
    ]);
  }
  return h('div', { class: 'qb-banner qb-banner-error', role: 'alert' }, [
    'QuickBooks connection failed or was cancelled. Please try again.',
  ]);
}

// The Settings QuickBooks section: shows connection status and offers connect /
// disconnect. Self-loading via runAsync, like the other settings blocks.
export function quickbooksSettingsSection() {
  const wrap = h('div');
  const statusHolder = h('div');

  const load = () =>
    runAsync(
      statusHolder,
      () => api.get('/quickbooks/status'),
      (status) => renderStatus(status),
      { loadingMessage: 'Checking QuickBooks…' },
    );

  const renderStatus = (status) =>
    status && status.connected ? renderConnected(status) : renderDisconnected();

  // --- Connected: show the company + environment, offer disconnect ----------
  function renderConnected(status) {
    const err = errorSlot();
    const actionHolder = h('div');

    const disconnectBtn = button('Disconnect', {});
    disconnectBtn.addEventListener('click', () => mount(actionHolder, confirmDisconnect()));
    mount(actionHolder, buttonRow([disconnectBtn]));

    function confirmDisconnect() {
      const cErr = errorSlot();
      const confirmBtn = button('Confirm disconnect', { variant: 'primary' });
      const cancelBtn = button('Cancel', {});
      cancelBtn.addEventListener('click', () => {
        mount(actionHolder, buttonRow([disconnectBtn]));
      });
      confirmBtn.addEventListener('click', async () => {
        const out = await runAction(confirmBtn, () => api.post('/quickbooks/disconnect', {}), {
          busyLabel: 'Disconnecting…',
          errSlot: cErr,
        });
        if (out) load();
      });
      return h('div', { class: 'qb-confirm' }, [
        muted('Disconnect QuickBooks? Quotes and invoices can’t be pushed until you reconnect. This doesn’t change anything already in QuickBooks.'),
        buttonRow([confirmBtn, cancelBtn]),
        cErr,
      ]);
    }

    return card([
      h('div', { class: 'qb-status' }, [
        statusBadge('connected'),
        chip(environmentLabel(status.environment), status.environment === 'production' ? 'warn' : null),
      ]),
      detailList([
        ['Company (realm) ID', status.realmId || null],
        ['Connected', shortDate(status.connectedAt)],
      ]),
      muted('The `app` stage connects to the QuickBooks sandbox company; `prod` connects to the live company.'),
      actionHolder,
      err,
    ], { title: 'QuickBooks' });
  }

  // --- Disconnected: offer connect (redirect to Intuit) ---------------------
  function renderDisconnected() {
    const err = errorSlot();
    const connectBtn = button('Connect QuickBooks', { variant: 'primary' });
    connectBtn.addEventListener('click', async () => {
      const original = connectBtn.textContent;
      connectBtn.disabled = true;
      connectBtn.textContent = 'Connecting…';
      setError(err, '');
      try {
        const res = await api.get('/quickbooks/connect');
        if (res && res.authorizeUrl) {
          // Hand the browser to Intuit's consent screen; it redirects back to
          // /settings?quickbooks=connected|error, handled on next boot.
          window.location.assign(res.authorizeUrl);
          return; // navigating away — leave the button in its busy state
        }
        setError(err, 'Could not start the QuickBooks connection. Please try again.');
      } catch (e) {
        setError(err, (e && e.message) || 'Could not start the QuickBooks connection.');
      }
      connectBtn.disabled = false;
      connectBtn.textContent = original;
    });

    return card([
      h('div', { class: 'qb-status' }, [statusBadge('not connected')]),
      muted('Connect your QuickBooks Online account to push quotes as Estimates and invoices as Invoices. You’ll confirm each push yourself — nothing is sent automatically.'),
      buttonRow([connectBtn]),
      err,
    ], { title: 'QuickBooks' });
  }

  load();

  const banner = takeQuickBooksReturnStatus();
  return mount(wrap, h('div', {}, [banner ? returnBanner(banner) : null, statusHolder]));
}

// --- Confirm-then-push card (Req 7.5, 7.6, NFR6.1) -------------------------

// The push action for a quote or invoice detail screen. `entity` is 'quote' or
// 'invoice'; `doc` is the loaded record; `onPushed(updated)` is called after a
// successful push (the detail screen refreshes). Already-synced documents (an
// existing qbEstimateId / qbInvoiceId) render as synced up front (Req 7.5).
export function quickbooksPushCard({ entity, doc, onPushed } = {}) {
  const isQuote = entity === 'quote';
  const idField = isQuote ? 'qbEstimateId' : 'qbInvoiceId';
  const qbType = isQuote ? 'Estimate' : 'Invoice';
  const pushPath = `/${entity}s/${encodeURIComponent(doc.id)}/push`;
  const holder = h('div');

  const isSynced = (record) => !!(record && record[idField]);

  // Perform the confirmed push. Keeps the local document view intact on failure
  // (the server does too — Req 7.6) and shows a state-specific error.
  async function doPush(confirmBtn) {
    const original = confirmBtn.textContent;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Pushing…';
    try {
      const updated = await api.post(pushPath, {});
      if (onPushed) onPushed(updated);
      else renderSynced(updated); // fallback when no refresh hook is supplied
    } catch (err) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = original;
      renderError(pushErrorInfo(err));
    }
  }

  // Synced: badge + QB id + last-synced, plus an idempotent "push again".
  function renderSynced(record) {
    const pushAgainBtn = button('Push again', {});
    pushAgainBtn.addEventListener('click', () => renderConfirm({ again: true }));
    mount(
      holder,
      card([
        h('div', { class: 'qb-status' }, [statusBadge('synced')]),
        detailList([
          [`QuickBooks ${qbType} ID`, record[idField]],
          ['Last synced', shortDate(record.qbSyncedAt)],
        ]),
        muted(`This ${entity} is in QuickBooks. Pushing again updates the existing ${qbType} — a duplicate is never created.`),
        buttonRow([pushAgainBtn]),
      ], { title: 'QuickBooks' }),
    );
  }

  // Idle: the entry point — an explicit push button (no automatic push, NFR6.1).
  function renderIdle() {
    const pushBtn = button('Push to QuickBooks', { variant: 'primary' });
    pushBtn.addEventListener('click', () => renderConfirm({ again: false }));
    mount(
      holder,
      card([
        muted(`Create this ${entity} in QuickBooks as an ${qbType}. You’ll confirm before anything is sent.`),
        buttonRow([pushBtn]),
      ], { title: 'QuickBooks' }),
    );
  }

  // Confirm step: the explicit human confirmation before the push happens.
  function renderConfirm({ again }) {
    const confirmBtn = button(again ? 'Confirm re-push' : 'Confirm push', { variant: 'primary' });
    const cancelBtn = button('Cancel', {});
    confirmBtn.addEventListener('click', () => doPush(confirmBtn));
    cancelBtn.addEventListener('click', () => (again ? renderSynced(doc) : renderIdle()));
    mount(
      holder,
      card([
        muted(
          again
            ? `This updates the existing QuickBooks ${qbType} with the current lines and totals.`
            : `This creates a new ${qbType} in QuickBooks for this ${entity}. Totals are taken from the saved ${entity}.`,
        ),
        h('div', { class: 'qb-confirm' }, [buttonRow([confirmBtn, cancelBtn])]),
      ], { title: 'QuickBooks' }),
    );
  }

  // Error state: message + the right recovery action for the failure kind.
  function renderError(info) {
    const actions = [];
    if (info.kind === 'reconnect') {
      actions.push(
        h('a', { class: 'btn btn-primary', href: routeToHash({ section: 'settings' }) }, ['Reconnect in Settings']),
      );
    }
    const retryBtn = button('Try again', { variant: info.kind === 'reconnect' ? 'secondary' : 'primary' });
    retryBtn.addEventListener('click', () => renderConfirm({ again: isSynced(doc) }));
    actions.push(retryBtn);

    mount(
      holder,
      card([
        h('div', { class: 'qb-status' }, [statusBadge(isSynced(doc) ? 'synced' : 'not connected')]),
        h('p', { class: 'qb-error', role: 'alert', text: info.message }),
        info.detail ? muted(info.detail) : null,
        buttonRow(actions),
      ], { title: 'QuickBooks' }),
    );
  }

  if (isSynced(doc)) renderSynced(doc);
  else renderIdle();

  return holder;
}
